import React, { useEffect } from 'react';
import {
  Colors,
  FontFamily,
  GoogleAuth, 
  Input,
  Main,
  Models,
  NavBar,
  PrimaryButton,
} from 'utils/imports.utils';
import Assets from 'imports/assets.import';
import { ButtonSize, ButtonTypes, GoogleAuthMethod } from 'utils/interface.utils';
import './log_in.screen.scss';
import { useNavigate } from 'react-router-dom';
import { toastifySuccess, useSetState } from 'utils/functions.utils';
import { useDispatch } from 'react-redux';
import { setUser } from 'store/reducers/userSlice';
import { AppDispatch } from 'store/store';
import { STRINGS } from 'constants/strings.constant';

interface User {
  _id: string;
  email: string;
  role: string;
  username: string;
  project: any[];
  is_deleted: boolean;
  created_at: string;
  modified_at: string;
  data?: any;
}

const ERROR_VISIBLE_MS = 5000;

const LogIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [state, setState] = useSetState({
    email: '',
    emailError: '',
    password: '',
    passWordError: '',
    activity: false,
    showPassword: false,
    isDisabled: true,
  });
  useEffect(() => {
    const disabled =
      !state.email.trim() || !state.password.trim() || state.activity;

    setState({ isDisabled: disabled });
  }, [state.email, state.password, state.activity]);

  const handleLogin = async (e: any) => {
    setState({ activity: true });

    try {
      const user = (await Models.auth.login({
        email: state.email,
        password: state.password,
      })) as User;
      if (user && user.data) {
        toastifySuccess('Login successfully!');
        dispatch(setUser({
          currentUser: {
            _id: user.data._id,
            email: user.data.email,
            name: user.data.username,
          }
        }))

        // Redirect based on role — exactly two roles exist: user, admin
        if (user.role === 'admin') {
          navigate('/admin'); // Redirect admin to dashboard
        } else {
          navigate('/welcome');
        }
      } else {
        // setState({ emailError: response.data.message || "Invalid credentials", passWordError: "" });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const message = typeof error === 'string' ? error : '';
      const validationPrefix = 'Error validating request body.';

      if (message === "User doesn't exist") {
        setState({ emailError: 'Invalid email', passWordError: '' });
      } else if (message === 'Incorrect password') {
        setState({ emailError: '', passWordError: 'Invalid password' });
      } else if (message === STRINGS.googleAuth.googleOnlyAccount) {
        setState({ emailError: message, passWordError: '' });
      } else if (message.startsWith(validationPrefix)) {
        // Joi message: 'Error validating request body. "password" length must be ...'
        const detail = message.slice(validationPrefix.length).trim();
        if (detail.includes('"password"')) {
          setState({ emailError: '', passWordError: detail });
        } else {
          setState({ emailError: detail || 'Login failed. Please try again.', passWordError: '' });
        }
      } else {
        setState({ emailError: 'Login failed. Please try again.', passWordError: '' });
      }
      // setState({ emailError: "Login failed. Please try again.", passWordError: "" });
    } finally {
      setState({ activity: false });
    }
  };

  // One message at the end of the form; the field it refers to gets a red border.
  const formError = state.emailError || state.passWordError;

  // The message and the red outline clear themselves after a few seconds.
  useEffect(() => {
    if (!formError) return;
    const timer = setTimeout(() => {
      setState({ emailError: '', passWordError: '' });
    }, ERROR_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [formError]);

  return (
    <Main
      backgroundColor={Colors.screenBackground}
      isRenderOnlyLogo={true}
      logoSize={{ width: '200px', height: '83px' }}>
      <div className="log_in">
        <div className="log_in_container">
          <div className="log_in_header">
            <div className="log_in_header_title">Log in</div>
            <div className="log_in_header_desc">
              Welcome back! Let’s get you in
            </div>
          </div>
          <div className="log_in_form">
            <div className="log_in_form_container">
              <div className={`log_in_form_input log_in_form_input_name ${state.emailError ? 'log_in_form_input_error' : ''}`}>
                <Input
                  placeholder="Enter your email"
                  label="Email"
                  type="email"
                  value={state.email}
                  onChange={(e) => setState({ email: e.target.value })}
                  error={state.emailError}
                />
              </div>
              <div className={`log_in_form_input log_in_form_input_password ${state.passWordError ? 'log_in_form_input_error' : ''}`}>
                <Input
                  placeholder="Enter your password"
                  label="Password"
                  // type="password"
                  type={state.showPassword ? 'text' : 'password'}
                  value={state.password}
                  onChange={(e) => setState({ password: e.target.value })}
                  icon={state.showPassword ? Assets.eye : Assets.eye_slash}
                  iconPosition="end"
                  iconOnPress={() =>
                    setState({ showPassword: !state.showPassword })
                  }
                  error={state.passWordError}
                />
              </div>
              {formError && (
                <div className="input_field_error log_in_form_error" role="alert">
                  <span className="log_in_form_error_icon" aria-hidden="true">!</span>
                  <span>{formError}</span>
                </div>
              )}
              <div className="log_in_form_forgot_password">
                <PrimaryButton
                  onClick={() => navigate('/forgot-password')}
                  fontFamily={FontFamily.medium}
                  lineHeight="24px"
                  color={Colors.primaryTextColor}
                  text="Forgot password?"
                  type={ButtonTypes.TEXT}
                  size={ButtonSize.SMALL}
                />
              </div>
            </div>
          </div>
          <div className="log_in_buttons">
            <div className="log_in_buttons_container">
              <div className="log_in_buttons_login">
                <PrimaryButton
                  // onClick={() => {}}
                  onClick={(e) => handleLogin(e)}
                  fontFamily={FontFamily.medium}
                  lineHeight="28px"
                  text="Log in"
                  size={ButtonSize.LARGE}
                  type={
                    state.isDisabled
                      ? ButtonTypes.DISABLED
                      : ButtonTypes.PRIMARY_GRADIENT
                  }
                  disabled={state.isDisabled}
                  activity={state.activity}
                />
              </div>
              <div className="log_in_buttons_separator_line">
                <div className="log_in_buttons_separator_line_text">or</div>
              </div>
              <div className="log_in_buttons_google">
                <GoogleAuth method={GoogleAuthMethod.LOGIN} />
              </div>
              <div className="log_in_buttons_already_have_account">
                <div className="log_in_buttons_already_have_account_text">
                  New here?
                </div>
                <PrimaryButton
                  // onClick={() => {}}
                  onClick={() => navigate('/signup')}
                  color={Colors.primaryTextColor}
                  fontFamily={FontFamily.semiBold}
                  lineHeight="24px"
                  textDecorationColor={Colors.primaryTextColor}
                  text="Sign up"
                  type={ButtonTypes.TEXT_UNDERLINE}
                  size={ButtonSize.SMALL}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Main>
  );
};

export default LogIn;
